import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import { dispatch, store } from 'store';
import UserConfigModel from 'types/userConfig/userConfig';
import { fetchCompanyCustomViews } from 'api/custom-views';
import { fetcher } from 'utils/axios';
import { updateSingleApplicationUser } from './applicationUsers';
import { handleChatUsersFilters } from './chat';
import { parseChatUsersLabelFilter } from 'utils/chat-users-label-filter';
import { DEFAULT_CONTACT_ARRIVAL_SOURCES } from 'utils/contact-arrival-sources';
import { getDefaultNotificationPreferences } from 'utils/notification-preferences';

const initialState: UserConfigModel = {
  isDisplayName: true,
  outgoingSenderDisplay: 'name',
  isAvatarActive: false,
  isDateTime: true,
  showDate: false,
  fontSize: '14',
  isMssgRTL: 'true',
  submitType: {
    ctrlEnter: [
      {
        //TODO text: currentLocale.settingsPopup.userConfig.tabs.chats.leftColumn
        //   .submitTypes.ctrlEnter.breakLine,
        text: 'Break line',
        checked: true,
      },
      {
        //TODO text: currentLocale.settingsPopup.userConfig.tabs.chats.leftColumn
        //   .submitTypes.ctrlEnter.sendMessage,
        text: 'Send message',
        checked: false,
      },
    ],
    shiftEnter: [
      {
        //TODO text: currentLocale.settingsPopup.userConfig.tabs.chats.leftColumn
        //   .submitTypes.shiftEnter.breakLine,
        text: 'Break line',
        checked: true,
      },
      {
        //TODO text: currentLocale.settingsPopup.userConfig.tabs.chats.leftColumn
        //   .submitTypes.shiftEnter.sendMessage,
        text: 'Send message',
        checked: false,
      },
    ],
    enter: [
      {
        //TODO text: currentLocale.settingsPopup.userConfig.tabs.chats.leftColumn
        //   .submitTypes.enter.breakLine,
        text: 'Break line',
        checked: false,
      },
      {
        //TODO text: currentLocale.settingsPopup.userConfig.tabs.chats.leftColumn
        //   .submitTypes.enter.sendMessage,
        text: 'Send message',
        checked: true,
      },
    ],
  },
  isNotifOn: true,
  enableSystemNotifications: true,
  notifHintTimeout: 5,
  displayInfoBots: false,
  displayInfoAssignee: false,
  audioNotifVol: 0.5,
  openChatAuthControl: true,
  predefinedAIPrompts: [],
  aiConnections: [],
  //* Filters
  unreadFilter: false,
  openCloseFilter: 'all',
  chatUsersLabelFilter: null,
  chatUsersSearchQuery: null,
  chatUsersDontDisturb: null,
  chatUsersTable: {
    columnsVisibility: {},
    columnsSizes: {},
  },
  //* Priority
  priorityContactsPagination: true,
  //* Views
  useViews: false,
  customViews: [],
  defaultChatViewHiddenForApplicationUserUdids: {},
  showUnreadPreviewOnHover: true,
  mockContactConversationsDemo: false,
  hebrewSpellCheckMode: 'off',
  hebrewSpellCheckCustomWords: [],
  whatsappSignatureTemplate: 'firstName_company',
  whatsappSignatureText: '',
  personalSignatureEnabled: false,
  personalSignatureText: '',
  //* ברירת מחדל כבויה כדי לא לשנות התנהגות קיימת למי שכבר משתמש בחתימה
  personalSignatureOnlyOnAgentChange: false,
  contactArrivalSources: [...DEFAULT_CONTACT_ARRIVAL_SOURCES],
  notificationPreferences: getDefaultNotificationPreferences(),
  persistLocalAiHistory: true,
  isKeyboardShortcutsEnabled: false,
};

const userConfigSlice = createSlice({
  name: 'userConfig',
  initialState,
  reducers: {
    updateUserConfig(state, action: PayloadAction<Partial<UserConfigModel>>) {
      Object.assign(state, action.payload);
    },
    resetUserConfig() {
      return initialState;
    },
  },
});

export default userConfigSlice.reducer;
export const { updateUserConfig, resetUserConfig } = userConfigSlice.actions;

/**
 * טוען את תצוגות החברה מהנוד (CompanyCustomViews) ושומר ל-store רק את הגלויות
 * למשתמש הנוכחי (רשימה לבנה visibleUserUdids). ה-store נשאר המקור הסינכרוני
 * שכל הצרכנים (chat.ts, סיידבר, מונים) קוראים ממנו.
 */
export async function loadCompanyCustomViews(): Promise<void> {
  try {
    const me = store.getState().user?.Udid;
    const allViews = await fetchCompanyCustomViews();
    const visibleViews = allViews.filter((view) =>
      Array.isArray(view.visibleUserUdids) ? Boolean(me && view.visibleUserUdids.includes(me)) : view.ownerUdid === me,
    );
    dispatch(updateUserConfig({ customViews: visibleViews }));
  } catch (error) {
    console.warn('loadCompanyCustomViews error: ', error);
  }
}

export async function getUserConfig() {
  try {
    const userData = store.getState().user;
    const userConfigData = await fetcher(
      'post',
      ['/api/services/GetUserConfig', { data: { userId: userData?.Udid } }],
      'history',
    );
    if (userConfigData.result && userConfigData.profileConfig) {
      const raw = JSON.parse(userConfigData.profileConfig) as Partial<UserConfigModel> & {
        enableCustomTime?: boolean;
      };
      if (raw.enableSystemNotifications === undefined && typeof raw.enableCustomTime === 'boolean') {
        raw.enableSystemNotifications = raw.enableCustomTime;
      }
      delete raw.enableCustomTime;
      // התצוגות המותאמות חיות בטבלת CompanyCustomViews — העותק הישן ב-ProfileConfig
      // קפוא כגיבוי בלבד ואסור שידרוס את ה-store (נטען ב-loadCompanyCustomViews)
      delete raw.customViews;
      if (
        raw.outgoingSenderDisplay == null ||
        !['none', 'name', 'photo'].includes(String(raw.outgoingSenderDisplay))
      ) {
        raw.outgoingSenderDisplay = raw.isDisplayName ? 'photo' : 'none';
      }
      if (raw.isDisplayName === undefined) {
        raw.isDisplayName = raw.outgoingSenderDisplay !== 'none';
      }
      const parsedUserConfig: Partial<UserConfigModel> = raw;
      dispatch(updateUserConfig(parsedUserConfig));
      // דגל "נטען מהשרת" — מבדיל בין "טרם נטען" ל"נטען בלי chatSidebarLayout"
      // (קובע אם לבצע מיגרציית localStorage חד-פעמית של מבנה הסרגל)
      dispatch(updateUserConfig({ userConfigLoadedFromServer: true }));

      // תצוגות החברה נטענות יחד עם הקונפיג — גם בטעינה ראשונית וגם ברענון מסוקט userConfig:views
      await loadCompanyCustomViews();

      if (userConfigData.permissionsConfig) {
        try {
          const perms = JSON.parse(userConfigData.permissionsConfig) as Record<string, boolean>;
          dispatch(updateUserConfig({ modulePermissions: perms }));
        } catch {
          // ignore malformed JSON
        }
      }

      if (userConfigData.viewsConfig) {
        try {
          const views = JSON.parse(userConfigData.viewsConfig) as Record<string, boolean>;
          dispatch(updateUserConfig({ userViewsConfig: views }));
        } catch {
          // ignore malformed JSON
        }
      }

      // Load chat filters from userConfig and apply them to chatUsersFilters
      if (parsedUserConfig.openCloseFilter || parsedUserConfig.unreadFilter !== undefined || 
          parsedUserConfig.chatUsersLabelFilter !== undefined || 
          parsedUserConfig.chatUsersSearchQuery !== undefined ||
          parsedUserConfig.chatUsersDontDisturb !== undefined) {
        const labelTokens = parseChatUsersLabelFilter(parsedUserConfig.chatUsersLabelFilter ?? null);
        dispatch(handleChatUsersFilters({
          open: parsedUserConfig.openCloseFilter || 'all',
          isOnlyUnread: parsedUserConfig.unreadFilter || false,
          labels: labelTokens,
          label: labelTokens[0] ?? '',
          searchQuery: parsedUserConfig.chatUsersSearchQuery || '',
          dontDisturb: parsedUserConfig.chatUsersDontDisturb || false,
        }));
      }
    }
  } catch (error) {
    console.warn('getUserConfig error: ', error);
  }
}
export async function fetchUpdateUserConfig(
  params: Partial<UserConfigModel>,
  sendNotification: boolean = true,
  userId?: string,
): Promise<boolean> {
  try {
    const currentUserConfig = store.getState().userConfig;
    const userData = store.getState().user;
    if (!currentUserConfig) {
      console.warn('fetchUpdateUserConfig: No user config yet');
      return false;
    }

    // customViews מנוהלות בטבלת CompanyCustomViews (api/custom-views) — לא נכתבות
    // יותר ל-ProfileConfig, אחרת כל שמירת הגדרות הייתה מחזירה את הרשימה הישנה
    const { customViews: _companyManagedViews, ...updatedUserConfigData } = {
      ...currentUserConfig,
      ...params,
    };
    const fetchParams = {
      userId: userId ? userId : userData?.Udid || '',
      optionsList: JSON.stringify(updatedUserConfigData),
    };

    const data = await fetcher('post', ['/api/services/UpdateUserConfig', { data: fetchParams }], 'history');
    if (data?.result) {
      // שידור רענון חי לקליינטים האחרים של המשתמש (תוסף / טאבים נוספים) דרך הנוד —
      // best-effort, לא חוסם את השמירה שכבר בוצעה
      void fetcher('post', ['/api/user-config/broadcast-updated', {}], 'servernode').catch(() => {});
    }
    if (data.result && (!userId || userId === userData?.Udid)) {
      dispatch(updateUserConfig(params));
    }
    dispatch(
      updateSingleApplicationUser({
        userId: userId ? userId : userData?.Udid || '',
        params: { ProfileConfig: JSON.stringify(updatedUserConfigData) },
      }),
    );
    if (sendNotification) {
      // הודעות על יצירה/עדכון/מחיקה של תצוגות עברו לקוראי api/custom-views
      enqueueSnackbar(data.result ? 'ההגדרות נשמרו בהצלחה' : 'שגיאה בשמירת ההגדרות', {
        variant: data.result ? 'success' : 'error',
      });
    }
    return Boolean(data?.result);
  } catch (error) {
    console.warn('fetchUpdateUserConfig err: ', error);
    if (sendNotification)
      enqueueSnackbar('שגיאה בעדכון ההגדרות', {
        variant: 'error',
      });
    return false;
  }
}
